import type { PublicationCode } from '@/scopes/common/domain/models'
import type {
  FiltersInQuickSearch,
  FiltersInQuickSearchValues,
  FilterType,
  QuickSearchApiClient,
  QuickSearchFilters,
} from '@/scopes/search/quick-search/domain/types'
import type { QuickSearchStoreApi } from '@/scopes/search/quick-search/infrastructure/store/store'
import { getFilterTypesForQuickSearch } from '@/scopes/search/quick-search/domain/policies/filters'

export function quickSearchActions({
  quickSearchApiClient,
  store,
}: {
  quickSearchApiClient: QuickSearchApiClient
  store: QuickSearchStoreApi
}) {
  async function filterConfirmed({ type, selection }: { type: FilterType, selection: FiltersInQuickSearchValues }) {
    const { setIn, publicationCode, contextItemId, filterSelection } = store.getState()

    if (
      (filterSelection[type] === undefined && selection === undefined)
      || JSON.stringify(filterSelection[type] ?? []) === JSON.stringify(selection)
    ) {
      return
    }

    const newSelection = {
      ...filterSelection,
      [type]: selection,
    }

    if (type === 'TransportType') {
      newSelection.DepartureDate = undefined
    }

    setIn('filterSelection', newSelection)
    setIn('getQuickSearch.loading', true)
    const { filterOptions, searchLinkRelative, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      filterSelection: newSelection,
    })
    setIn('getQuickSearch.loading', false)
    setIn('searchLink', searchLinkRelative || searchLink)
    setIn('totalResults', totalResults)
    setIn('filterOptions', filterOptions)
  }

  async function initQuickSearch({
    publicationCode,
    contextItemId,
    initialSelection,
  }: {
    publicationCode: string
    contextItemId: string
    initialSelection: QuickSearchFilters
  }) {
    const { setIn } = store.getState()

    setIn('publicationCode', publicationCode)
    setIn('contextItemId', contextItemId)

    setIn('filterSelection', initialSelection)
    setIn('getQuickSearch.loading', true)

    const requiredFilters = getFilterTypesForQuickSearch(publicationCode as PublicationCode)

    const { filterOptions, searchLinkRelative, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      requiredFilters,
      filterSelection: initialSelection,
    })
    setIn('getQuickSearch.loading', false)
    setIn('searchLink', searchLinkRelative || searchLink)
    setIn('totalResults', totalResults)
    setIn('filterOptions', filterOptions)
  }

  async function switchPublicationCode(publicationCode: string, selection: Partial<FiltersInQuickSearch>) {
    const { setIn, contextItemId } = store.getState()

    setIn('publicationCode', publicationCode)
    setIn('getQuickSearch.loading', true)
    setIn('filterSelection', selection)

    const requiredFilters = getFilterTypesForQuickSearch(publicationCode as PublicationCode)

    const { filterOptions, searchLinkRelative, searchLink, totalResults } = await quickSearchApiClient.getQuickSearch({
      publicationCode,
      contextItemId,
      requiredFilters,
      filterSelection: selection,
    })
    setIn('getQuickSearch.loading', false)
    setIn('searchLink', searchLinkRelative || searchLink)
    setIn('totalResults', totalResults)
    setIn('filterOptions', filterOptions)
  }

  return {
    filterConfirmed,
    initQuickSearch,
    switchPublicationCode,
  }
}
